// utils/proposalContentParser.js
// Pure utility functions for converting between Markdown, HTML, and structured proposal content.
// Moved out of the component to avoid recreation on every render and to enable unit testing.

/**
 * Convert a Markdown-ish string to HTML.
 * Handles headings, lists, bold/italic, images, and pipe-tables.
 */
export const parseMarkdownToHTML = (text) => {
    if (!text) return "";
  
    let html = String(text).replace(/\r\n/g, "\n");
  
    // If the string already contains HTML block tags (and is not just inline span/pre),
    // return as-is to avoid double-parsing.
    if (
      /<(div|table|ul|ol)[ >]/i.test(html) &&
      !/<span/i.test(html) &&
      !/<pre>/i.test(html)
    ) {
      return html;
    }
  
    // Unordered lists
    html = html.replace(/^[*\-•]\s+(.*)$/gm, "<ul><li>$1</li></ul>");
    html = html.replace(/<\/ul>\s*<ul>/g, "");
  
    // Ordered lists
    html = html.replace(/^\d+\.\s+(.*)$/gm, "<ol><li>$1</li></ol>");
    html = html.replace(/<\/ol>\s*<ol>/g, "");
  
    // Headings
    html = html.replace(/^(#{1,6})\s+(.*)$/gm, (_m, hashes, content) => {
      const level = hashes.length;
      return `<h${level}>${content}</h${level}>`;
    });
  
    // Markdown images
    html = html.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" style="max-width: 100%; height: auto;" />'
    );
  
    // Bold and italic
    html = html.replace(/\*\*([\s\S]*?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/\*([^*\n]+)\*/g, "<em>$1</em>");
  
    // Tables
    const lines = html.split("\n");
    let inTable = false;
    let tableHtml = "";
    const parsedLines = [];
    let isFirstRow = true;
  
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
  
      if (line.startsWith("|") && line.endsWith("|") && line.length > 1) {
        if (!inTable) {
          inTable = true;
          isFirstRow = true;
          tableHtml = "<table>\n<tbody>\n";
        }
  
        // Skip separator row
        if (/^\|[\s\-|:]+\|$/.test(line)) {
          isFirstRow = false;
          continue;
        }
  
        const cells = line.split("|").slice(1, -1);
        tableHtml += "<tr>\n";
        cells.forEach((cell) => {
          tableHtml += isFirstRow
            ? `<th>${cell.trim()}</th>\n`
            : `<td>${cell.trim()}</td>\n`;
        });
        tableHtml += "</tr>\n";
      } else {
        if (inTable) {
          tableHtml += "</tbody>\n</table>";
          parsedLines.push(tableHtml);
          inTable = false;
          tableHtml = "";
        }
        parsedLines.push(lines[i]);
      }
    }
  
    if (inTable) {
      tableHtml += "</tbody>\n</table>";
      parsedLines.push(tableHtml);
    }
  
    html = parsedLines.join("\n");
  
    // Wrap orphan blocks in <p>
    const blocks = html.split(/\n\n+/);
    return blocks
      .map((block) => {
        const trimmed = block.trim();
        if (!trimmed) return "";
        if (/^(<h[1-6]>|<ul>|<ol>|<p>|<table|<pre>|<img)/i.test(trimmed)) {
          return trimmed;
        }
        return `<p>${trimmed.replace(/\n/g, "<br/>")}</p>`;
      })
      .filter(Boolean)
      .join("\n\n");
  };
  
  /**
   * Convert a single DOM node to a Markdown string.
   */
  const nodeToMarkdown = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent.replace(/\u00A0/g, " ");
    }
  
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
  
    const tag = node.tagName.toLowerCase();
    const inner = Array.from(node.childNodes).map(nodeToMarkdown).join("");
  
    switch (tag) {
      case "p":
        return `${inner}\n\n`;
      case "strong":
      case "b":
        return `**${inner}**`;
      case "em":
      case "i":
        return `*${inner}*`;
      case "ul":
      case "ol":
        return `${inner}\n`;
      case "li": {
        const parent = node.parentNode;
        if (parent && parent.tagName.toLowerCase() === "ol") {
          const index = Array.from(parent.children).indexOf(node) + 1;
          return `${index}. ${inner}\n`;
        }
        return `• ${inner}\n`;
      }
      case "br":
        return "\n";
      case "h1":
        return `# ${inner}\n\n`;
      case "h2":
        return `## ${inner}\n\n`;
      case "h3":
        return `### ${inner}\n\n`;
      case "h4":
        return `#### ${inner}\n\n`;
      case "h5":
        return `##### ${inner}\n\n`;
      case "h6":
        return `###### ${inner}\n\n`;
      case "pre":
        return `<pre>${inner}</pre>\n\n`;
      case "img": {
        const alt = node.getAttribute("alt") || "image";
        const src = node.getAttribute("src") || "";
        return `![${alt}](${src})`;
      }
      case "span": {
        let styleStr = "";
        if (node.style.color) styleStr += `color: ${node.style.color}; `;
        if (node.style.backgroundColor) {
          styleStr += `background-color: ${node.style.backgroundColor};`;
        }
        if (styleStr.trim()) {
          return `<span style="${styleStr.trim()}">${inner}</span>`;
        }
        return inner;
      }
      case "table": {
        let tableMd = "";
        const rows = Array.from(node.querySelectorAll("tr"));
        rows.forEach((row, i) => {
          const cells = Array.from(row.querySelectorAll("th, td")).map((c) =>
            c.textContent.trim()
          );
          if (cells.length > 0) {
            tableMd += `| ${cells.join(" | ")} |\n`;
            if (i === 0) {
              tableMd += `|${cells.map(() => "---|").join("")}\n`;
            }
          }
        });
        return `${tableMd}\n`;
      }
      default:
        return inner;
    }
  };
  
  /**
   * Build a heading from a child element matched against the section regex.
   * Reusable across section/child detection branches.
   */
  const buildHeadingTitle = (child, match, headingPrefix) => {
    const number = match[1].replace(/\.+$/, "");
    const fullLineMarkdown = nodeToMarkdown(child).trim();
    const titleText = match[2];
  
    // Preserve italic/bold wrappers if the entire line was wrapped
    if (fullLineMarkdown.startsWith("*") && fullLineMarkdown.endsWith("*")) {
      return { number, title: `${headingPrefix}*${titleText}*` };
    }
    if (fullLineMarkdown.startsWith("**") && fullLineMarkdown.endsWith("**")) {
      return { number, title: `${headingPrefix}**${titleText}**` };
    }
  
    // Strip the section number from the rendered text
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = child.innerHTML;
  
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    let found = false;
    while (walker.nextNode()) {
      if (!found && walker.currentNode.nodeValue.includes(match[1])) {
        walker.currentNode.nodeValue = walker.currentNode.nodeValue.replace(
          new RegExp(`^\\s*${match[1].replace(/\./g, "\\.")}[.\\s]*`),
          ""
        );
        found = true;
        break;
      }
    }
  
    return {
      number,
      title: headingPrefix + nodeToMarkdown(tempDiv).trim(),
    };
  };
  
  const HEADING_PREFIX_MAP = {
    h1: "# ",
    h2: "## ",
    h3: "### ",
    h4: "#### ",
    h5: "##### ",
    h6: "###### ",
  };
  
  /**
   * Parse the editor's HTML output back into the structured proposal format
   * the API expects: [{ number, title, response, children: [...] }, ...]
   */
  export const parseHTMLToStructuredContent = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
  
    const structuredContent = [];
    let currentSection = null;
    let currentChild = null;
  
    Array.from(doc.body.children).forEach((child) => {
      const tag = child.tagName.toLowerCase();
      const cleanText = child.textContent.trim();
  
      let isSection = false;
      let isChild = false;
      const match = cleanText.match(/^([\d]+(?:\.[\d]+)*\.{0,2})[.\s]+(.*)$/);
  
      if ((/^h[1-6]$/.test(tag) || tag === "p") && match) {
        const numStr = match[1];
        const potentialTitle = match[2];
        if (potentialTitle.length < 150 && !potentialTitle.endsWith(".")) {
          if (/\d+\.\d+/.test(numStr)) isChild = true;
          else isSection = true;
        }
      }
  
      if (isSection) {
        // Section headings default to h2 prefix unless tag is different
        const prefix =
          tag === "h2" ? "" : HEADING_PREFIX_MAP[tag] || "";
        const { number, title } = match
          ? buildHeadingTitle(child, match, prefix)
          : {
              number: `${structuredContent.length + 1}`,
              title: prefix + nodeToMarkdown(child).trim(),
            };
  
        currentSection = { number, title, response: "", children: [] };
        currentChild = null;
        structuredContent.push(currentSection);
        return;
      }
  
      if (isChild) {
        // Child headings default to h3 prefix unless tag is different
        const prefix =
          tag === "h3" ? "" : HEADING_PREFIX_MAP[tag] || "";
        const { number, title } = match
          ? buildHeadingTitle(child, match, prefix)
          : {
              number: `${currentSection?.number || 1}.${
                (currentSection?.children?.length || 0) + 1
              }`,
              title: prefix + nodeToMarkdown(child).trim(),
            };
  
        currentChild = { number, title, response: "" };
        if (currentSection) {
          currentSection.children.push(currentChild);
        } else {
          currentSection = {
            number: "1",
            title: "General",
            response: "",
            children: [currentChild],
          };
          structuredContent.push(currentSection);
        }
        return;
      }
  
      // Body content
      if (cleanText === "No content available") return;
  
      const markdown = nodeToMarkdown(child);
      if (currentChild) {
        currentChild.response += markdown;
      } else if (currentSection) {
        currentSection.response += markdown;
      } else if (markdown.trim() || child.querySelector("img")) {
        currentSection = {
          number: "1",
          title: "General",
          response: markdown,
          children: [],
        };
        structuredContent.push(currentSection);
      }
    });
  
    // Trim whitespace from all responses
    structuredContent.forEach((sec) => {
      sec.response = sec.response.trim();
      sec.children.forEach((c) => {
        c.response = c.response.trim();
      });
    });
  
    return structuredContent;
  };

  /**
   * Convert a single block of editor HTML (e.g. one section's body) back into
   * the Markdown the API stores in `response`. Reuses the same per-node
   * conversion as parseHTMLToStructuredContent, without the section
   * re-structuring — so headings/lists/bold/tables survive a save round-trip.
   */
  export const htmlToMarkdown = (html) => {
    if (!html) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return Array.from(doc.body.childNodes)
      .map(nodeToMarkdown)
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  /**
   * Convert API proposal_content array to a single HTML string for the editor.
   */
  export const convertProposalContentToHTML = (proposalContent) => {
    if (!Array.isArray(proposalContent)) return "";
  
    let html = "";
  
    proposalContent.forEach((section) => {
      const sectionNum = section.number.endsWith(".")
        ? section.number
        : `${section.number}.`;
  
      let sTag = "h2";
      let cleanTitle = section.title || "";
  
      const headingMatch = cleanTitle.match(/^(#{1,6})\s+(.*)/);
      if (headingMatch) {
        sTag = `h${headingMatch[1].length}`;
        cleanTitle = headingMatch[2];
      }
  
      const parsedTitle = parseMarkdownToHTML(cleanTitle)
        .replace(/^<p>/, "")
        .replace(/<\/p>$/, "")
        .trim();
  
      html += `<${sTag}>${sectionNum} ${parsedTitle}</${sTag}>\n`;
  
      if (section.response && section.response.trim() !== "") {
        html += `${parseMarkdownToHTML(section.response)}\n`;
      }
  
      if (section.children && section.children.length > 0) {
        section.children.forEach((c) => {
          let cTag = "h3";
          let cleanChildTitle = c.title || "";
  
          const childHeadingMatch = cleanChildTitle.match(/^(#{1,6})\s+(.*)/);
          if (childHeadingMatch) {
            cTag = `h${childHeadingMatch[1].length}`;
            cleanChildTitle = childHeadingMatch[2];
          }
  
          const parsedChildTitle = parseMarkdownToHTML(cleanChildTitle)
            .replace(/^<p>/, "")
            .replace(/<\/p>$/, "")
            .trim();
  
          html += `<${cTag}>${c.number} ${parsedChildTitle}</${cTag}>\n`;
  
          if (c.response && c.response.trim() !== "") {
            html += `${parseMarkdownToHTML(c.response)}\n`;
          } else {
            html += `<p><em>No content available</em></p>\n`;
          }
        });
      }
  
      html += "\n";
    });
  
    return html;
  };
  
  /**
   * Parse a period-separated list string from the analysis API into an array.
   */
  export const parseListString = (str) => {
    if (!str) return [];
    return str
      .split(/\.\s+/)
      .filter((item) => item.trim().length > 0)
      .map((item) => item.trim() + (item.endsWith(".") ? "" : "."));
  };